import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Trash2, Pencil, Store as StoreIcon, ExternalLink, Sparkles } from "lucide-react";
import { api } from "../../lib/api";
import { Modal, IconBtn, AddBtn, inputCls } from "./ui";
import { AISuggestCategoriesModal } from "./AISuggestCategoriesModal";
import StoreWizard from "./StoreWizard";

export default function Stores() {
  const [stores, setStores] = useState([]);
  const [categories, setCategories] = useState([]);
  const [form, setForm] = useState(null);
  const [ai, setAi] = useState(null);
  const [wizard, setWizard] = useState(false);
  const load = async () => { const { data } = await api.get("/admin/stores"); setStores(data); };
  useEffect(() => {
    load();
    api.get("/admin/categories").then((r) => setCategories(r.data || [])).catch(() => {});
  }, []);
  const del = async (id) => { if (!window.confirm("Delete this store?")) return; await api.delete(`/admin/stores/${id}`); toast.success("Store deleted"); load(); };

  return (
    <div data-testid="admin-stores">
      <div className="flex items-center justify-between gap-2 mb-4">
        <p className="text-sm text-[#5C5C5C] max-w-lg">Onboard the stores you source from. Onboarding pulls a store's categories and imports products, auto-mapping them into your storefront taxonomy.</p>
        <div className="flex gap-2 shrink-0">
          <AddBtn label="Onboard store" onClick={() => setWizard(true)} testid="onboard-store-btn" icon={StoreIcon} />
          <AddBtn label="Add store" onClick={() => setForm({})} testid="add-store-btn" />
        </div>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {stores.length === 0 && <p className="text-[#5C5C5C]">No stores yet. Add Boots, Tesco, Costco…</p>}
        {stores.map((s) => (
          <div key={s.id} className="bg-white border border-black/5 rounded-2xl p-5" data-testid={`store-row-${s.id}`}>
            <div className="flex items-start justify-between gap-2">
              <div className="flex items-center gap-2 min-w-0">
                <span className="w-9 h-9 rounded-full bg-[#306D29]/10 text-[#306D29] flex items-center justify-center shrink-0"><StoreIcon size={17} /></span>
                <div className="min-w-0">
                  <p className="font-display text-lg text-[#0D530E] truncate">{s.name}</p>
                  <p className="text-xs text-[#5C5C5C]">{s.category || "—"} · {s.currency}</p>
                </div>
              </div>
              <span className={`text-[10px] font-bold uppercase px-2 py-0.5 rounded-full shrink-0 ${s.status === "active" ? "bg-[#2D7A5D]/10 text-[#2D7A5D]" : "bg-black/5 text-[#5C5C5C]"}`}>{s.status}</span>
            </div>
            <div className="flex items-center justify-between mt-4 pt-3 border-t border-black/5">
              <div className="text-sm">
                <div><span className="font-semibold text-[#0D530E]">{s.product_count}</span> <span className="text-[#5C5C5C]">products</span>
                {s.website && <a href={s.website.startsWith("http") ? s.website : `https://${s.website}`} target="_blank" rel="noreferrer" className="ml-3 text-[#306D29] inline-flex items-center gap-1 text-xs">site <ExternalLink size={12} /></a>}</div>
                <p className="text-[11px] text-[#5C5C5C] mt-0.5" data-testid={`store-lastimport-${s.id}`}>{s.last_imported_at ? `Last import ${new Date(s.last_imported_at).toLocaleDateString()}` : "No imports yet"}</p>
              </div>
              <div className="flex gap-0.5">
                <IconBtn title="AI categories" testid={`store-ai-${s.id}`} onClick={() => setAi({ store: s.name, website: s.website || "" })}><Sparkles size={15} /></IconBtn>
                <IconBtn title="Edit" testid={`store-edit-${s.id}`} onClick={() => setForm(s)}><Pencil size={15} /></IconBtn>
                <IconBtn title="Delete" danger testid={`store-del-${s.id}`} onClick={() => del(s.id)}><Trash2 size={15} /></IconBtn>
              </div>
            </div>
          </div>
        ))}
      </div>
      {form && <StoreForm store={form} onClose={() => setForm(null)} onSaved={() => { setForm(null); load(); }} />}
      {ai && <AISuggestCategoriesModal store={ai.store} website={ai.website} onClose={() => setAi(null)} onApplied={() => setAi(null)} />}
      {wizard && <StoreWizard categories={categories} onClose={() => setWizard(false)} onDone={() => { setWizard(false); load(); }} />}
    </div>
  );
}

function StoreForm({ store, onClose, onSaved }) {
  const editing = !!store.id;
  const [f, setF] = useState({
    name: store.name || "", category: store.category || "", currency: store.currency || "GBP",
    website: store.website || "", status: store.status || "active", notes: store.notes || "",
  });
  const [saving, setSaving] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const submit = async (e) => {
    e.preventDefault(); setSaving(true);
    try {
      if (editing) await api.put(`/admin/stores/${store.id}`, f); else await api.post("/admin/stores", f);
      toast.success(editing ? "Store saved" : "Store added"); onSaved();
    } catch { toast.error("Failed to save store"); setSaving(false); }
  };
  return (
    <Modal title={editing ? "Edit store" : "Add store"} onClose={onClose} testid="store-form">
      <form onSubmit={submit} className="space-y-3">
        <input required className={inputCls} placeholder="Store name (e.g. Boots UK)" value={f.name} onChange={set("name")} data-testid="sf-name" />
        <div className="grid grid-cols-2 gap-3">
          <input className={inputCls} placeholder="Category (e.g. Beauty)" value={f.category} onChange={set("category")} data-testid="sf-category" />
          <input className={inputCls} placeholder="Currency" value={f.currency} onChange={set("currency")} />
        </div>
        <input className={inputCls} placeholder="Website (boots.com)" value={f.website} onChange={set("website")} data-testid="sf-website" />
        <textarea className={inputCls} rows={2} placeholder="Notes" value={f.notes} onChange={set("notes")} />
        <select className={inputCls} value={f.status} onChange={set("status")} data-testid="sf-status">
          <option value="active">Active</option>
          <option value="inactive">Inactive</option>
        </select>
        <button type="submit" disabled={saving} data-testid="sf-submit" className="w-full bg-[#306D29] hover:bg-[#0D530E] text-white font-semibold py-3 rounded-full disabled:opacity-60" style={{ transition: "background-color 0.2s ease" }}>{saving ? "Saving…" : editing ? "Save store" : "Add store"}</button>
      </form>
    </Modal>
  );
}
